import React, { useState, useEffect, useLayoutEffect } from "react";
import { db } from "./firebase";
import { collection, onSnapshot } from "firebase/firestore";

// ======= CONFIG (easy to tweak) =======
const DEFAULT_REVEAL_DWELL_SECS =
  Number(localStorage.getItem("opt_reveal_dwell_secs") || 30); // was 20

// Stop the “pumping” after this many ms
const PUMP_STOP_MS = 5000;

// Fallback selfie image (inline SVG) — guarantees no “missing image”
const DEFAULT_SELFIE =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256">
  <rect width="100%" height="100%" rx="32" ry="32" fill="#1c2438"/>
  <circle cx="128" cy="100" r="52" fill="#4c6aff"/>
  <rect x="44" y="156" width="168" height="80" rx="28" fill="#4c6aff"/>
</svg>`);

// Helper to normalize the question type string
const normalizeType = (q) => String(q?.type || q?.CAT || "").toUpperCase();

// --- Team Card (square selfie only; always shows an <img>) ---
function TeamCard({ selfieUrl }) {
  const safeSrc = selfieUrl || DEFAULT_SELFIE;
  return (
    <div
      style={{
        width: "80px",
        height: "80px",
        flexShrink: 0,
        background: "rgba(10, 12, 20, 0.85)",
        borderRadius: "16px",
        border: "2px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 4px 18px rgba(0,0,0,0.5)",
        overflow: "hidden",
      }}
    >
      <img
        src={safeSrc}
        alt="team selfie"
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        onError={(e) => {
          // Guarantee we never show a broken/missing image
          if (e?.target) e.target.src = DEFAULT_SELFIE;
        }}
      />
    </div>
  );
}

// --- MAIN Answer Reveal OVERLAY ---
export default function AnswerReveal({ q, questionIdx, gameCode, onClose }) {
  const [answers, setAnswers] = useState([]);
  const [teamInfo, setTeamInfo] = useState({});
  const [countdown, setCountdown] = useState(DEFAULT_REVEAL_DWELL_SECS);
  const [pillData, setPillData] = useState([]);

  // Data subscriptions
  useEffect(() => {
    if (!gameCode || typeof questionIdx === "undefined") return;

    const unsubAnswers = onSnapshot(collection(db, "games", gameCode, "answers"), (snap) => {
      const all = [];
      snap.forEach((doc) => {
        if (doc.data().q === questionIdx) all.push(doc.data());
      });
      setAnswers(all);
    });

    const unsubTeams = onSnapshot(collection(db, "games", gameCode, "teams"), (snap) => {
      const info = {};
      snap.forEach((doc) => {
        info[doc.id] = doc.data();
      });
      setTeamInfo(info);
    });

    return () => {
      unsubAnswers();
      unsubTeams();
    };
  }, [questionIdx, gameCode]);

  // Keyboard: → to close immediately
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Auto-advance (longer dwell)
  useEffect(() => {
    if (countdown <= 0) {
      onClose();
      return;
    }
    const timerId = setTimeout(() => setCountdown((s) => s - 1), 1000);
    return () => clearTimeout(timerId);
  }, [countdown, onClose]);

  // 5s pump limiter: remove “pulse-correct” after PUMP_STOP_MS
  useEffect(() => {
    const correctEls = Array.from(
      document.querySelectorAll(
        ".answer-option.pulse-correct, .player-answer-option.pulse-correct"
      )
    );
    if (!correctEls.length) return;
    const stopTO = setTimeout(() => {
      correctEls.forEach((el) => el.classList.remove("pulse-correct"));
      // keep the “correct” border, just stop the animation
    }, PUMP_STOP_MS);
    return () => clearTimeout(stopTO);
  }, []);

  // Position overlays and compute stats for each visible pill
useLayoutEffect(() => {
    // Wait for CSS pulse (1.22s * 2) to finish before measuring
    const PULSE_FINISH_MS = 2500; // 2.5 seconds

    const timerId = setTimeout(() => {
      if (!answers.length || !q || !Array.isArray(q.answers)) return;

      const totalAnswers = answers.length;
      const type = normalizeType(q);
      const isMulti = type === "MULTIANSWER";

      // Group by chosen index
      const grouped = {};
      q.answers.forEach((_, index) => {
        grouped[index] = [];
      });

      answers.forEach((ans) => {
        if (isMulti) {
          // For MULTIANSWER, ans.answer is an array [0, 2, 5]
          // Add this team to the group for *each* answer they selected
          if (Array.isArray(ans.answer)) {
            ans.answer.forEach(chosenIndex => {
              if (typeof grouped[chosenIndex] !== "undefined") {
                grouped[chosenIndex].push(ans);
              }
            });
          }
        } else {
          // For single-choice questions, ans.answer is just a number
          const chosenIndex = ans.answer;
          if (typeof grouped[chosenIndex] !== "undefined") {
            grouped[chosenIndex].push(ans);
          }
        }
      });

const answersArr = Array.isArray(q?.answers) ? q.answers : [];
      const order = Array.isArray(q.answersOrder) ? q.answersOrder.slice() : Array.from({ length: answersArr.length }, (_, i) => i);

      // --- REVISED SECTION START ---
      const answerStats = order.map((originalIndex, displayIndex) => {
        // Get all submissions where this option was selected (this part is likely correct)
        const submissionsForThisOption = grouped[originalIndex] || [];

        // *** FIX 1: Count UNIQUE teams for percentage ***
        const uniqueTeamsWhoChose = new Set(submissionsForThisOption.map(ans => ans.team));
        const countUniqueTeams = uniqueTeamsWhoChose.size;
        const percentage = totalAnswers > 0 ? (countUniqueTeams / totalAnswers) * 100 : 0;
        // *** END FIX 1 ***

        // (Keep the isCorrect, letter, colorClass logic the same)
        const rawAnswerText = q.answers[originalIndex];
        const isCorrect = isMulti
          ? /^\s*\[.*?\]\s*$/.test(String(rawAnswerText))
          : originalIndex === 0;
        const letter = String.fromCharCode(65 + displayIndex);
        const colorClass = `option-${letter.toLowerCase()}`;

        // *** FIX 2: Ensure teamData uses the correctly grouped submissions ***
        // This part should already be correct IF the grouping worked.
        // It maps over ALL submissions linked to this option index.
        const teamData = submissionsForThisOption
          .map((t) => ({
            ...t, // Spread the submission data (includes team name, points etc.)
            selfieUrl: teamInfo[t.team]?.selfieUrl || null, // Add selfie URL
          }))
          .sort((a, b) => (b.points ?? 0) - (a.points ?? 0)); // Sort by points
        // *** END FIX 2 ***

        return {
           id: originalIndex,
           // Use the correctly calculated unique count for percentage
           percentage,
           // Use the full list of submissions for team display
           teams: teamData,
           isCorrect,
           colorClass,
           displayIndex
        };
      });

      const pillElements = document.querySelectorAll(".answer-option");
      const newPillData = [];

      answerStats.forEach((stats) => {
        const element = Array.from(pillElements).find((el) => el.classList.contains(stats.colorClass));
        if (element) {
          const rect = element.getBoundingClientRect();

          // how wide the % fill should appear (respecting rounded ends)
          const pillPadding = rect.height;
          const fillableWidth = Math.max(1, rect.width - pillPadding);
          const visualPercentage = Math.min(100, (stats.percentage / 100) * (rect.width / fillableWidth) * 100);

          // determine if we have room to scroll horizontally
          const CARD_WIDTH = 90;
          const CARD_MARGIN = 16;
          const totalImageWidth = stats.teams.length * (CARD_WIDTH + CARD_MARGIN);
          const availableWidth = window.innerWidth - rect.right - 40;
          const pixelsPerSecond = 80;
          const scrollDistance = Math.max(0, totalImageWidth + availableWidth);
          const marqueeDuration = Math.max(8, scrollDistance / pixelsPerSecond);

          newPillData.push({
            ...stats,
            rect,
            disableScroll: totalImageWidth <= availableWidth,
            visualPercentage,
            totalImageWidth,
            availableWidth,
            marqueeDuration,
          });
        }
      });

      setPillData(newPillData);
    }, PULSE_FINISH_MS);

    // Add a cleanup function for the timer
    return () => clearTimeout(timerId);
    
  }, [answers, teamInfo, q]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        pointerEvents: "none",
      }}
    >
      {/* Local styles for overlay visuals (glimmer + marquee) */}
      <style>{`
        /* Filled region with animated glimmer sheen */
        .glimmer-overlay {
          position: absolute;
          top: 0; left: 0; height: 100%;
          background: rgba(255, 255, 255, 0.18);
          border-radius: 999px;
          transition: width 900ms cubic-bezier(0.25, 1, 0.5, 1);
          overflow: hidden;
        }
        .glimmer-overlay > .reveal-glimmer-sheen {
          position: absolute;
          top: 0; bottom: 0; width: 40%;
          pointer-events: none;
          transform: translateX(-100%);
          background: linear-gradient(120deg,
            rgba(255,255,255,0.00) 0%,
            rgba(255,255,255,0.35) 45%,
            rgba(255,255,255,0.80) 55%,
            rgba(255,255,255,0.35) 65%,
            rgba(255,255,255,0.00) 100%);
          animation: sheen-slide 1.25s linear infinite;
        }
        @keyframes sheen-slide {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(160%); }
        }

        /* Marquee that starts off the right edge, then scrolls left (single pass) */
        .scroll-row-horizontal {
          display: flex;
          position: absolute;
          left: 0;
          transform: translateX(var(--marquee-from, 100%)); /* start off-screen at right */
          animation: marquee var(--marquee-duration, 20s) linear forwards;
          will-change: transform;
        }
        @keyframes marquee {
          from { transform: translateX(var(--marquee-from, 100%)); }  /* enter from right edge */
          to   { transform: translateX(var(--marquee-to, -100%)); }   /* leave past left edge */
        }

        @keyframes reveal-roll-in {
                  from { transform: translateX(100vw); }
                  to   { transform: translateX(0); }
                }
      @keyframes pop-in {
            from { opacity: 0; transform: translate(-50%, -50%) scale(0.5); } /* Should be -50% for X */
            to   { opacity: 1; transform: translate(-50%, -50%) scale(1); } /* Should be -50% for X */
          }
      `}</style>


{/* Per-pill overlays */}
      {pillData.map((data) => {
        // Destructure props including shouldSlide and displayIndex
        const { id, rect, teams, percentage, isCorrect, visualPercentage, disableScroll, displayIndex, shouldSlide, totalImageWidth, availableWidth, marqueeDuration } = data;

        const scrollingTeams = teams;

        // Fixed delay: 5s pulse + 0.6s slide/fade = 5600ms
        const REVEAL_START_DELAY_MS = 1100;

        // Define the slide transform if needed
        const slideTransform = shouldSlide ? 'translateX(-150px)' : 'translateX(0)';
        // Define the transition ONLY if sliding, otherwise none for transform
        const slideTransition = shouldSlide ? 'transform 0.6s cubic-bezier(0.25, 1, 0.5, 1)' : 'none';

        // Base animation style for roll-in
        const baseAnimStyle = {
          animation: "reveal-roll-in 750ms cubic-bezier(0.25, 1, 0.5, 1) forwards",
          animationDelay: `${REVEAL_START_DELAY_MS + displayIndex * 100}ms`,
          transform: 'translateX(100vw)', // Initial state for roll-in animation
          // Apply slide transition if needed (will run after roll-in completes)
          transition: slideTransition,
        };

        // Combine base animation style with the conditional slide transform
        const finalAnimStyle = {
          ...baseAnimStyle,
          transform: `${baseAnimStyle.transform} ${slideTransform}`, // Combine initial off-screen with potential slide
        };
        
        // --- FIX: Calculate the required offset to sit over the rounded end ---
        const MEDAL_SIZE = rect.height * 1.15;
        const MEDAL_RADIUS = MEDAL_SIZE / 2;
        // Transform X needs to be:
        // 1. Half its own width to center (50% of MEDAL_SIZE)
        // 2. Plus half the pill height (radius) to tuck it back in over the pill's rounded end.
        // We use -MEDAL_RADIUS for centering and -MEDAL_RADIUS for the coverage offset.
        // Total transform X = calc(-50% - ${MEDAL_RADIUS}px)
        const totalTransformX = `calc(-50% - ${MEDAL_RADIUS}px)`;


        return (
          // Add key prop here using original answer ID and current question index
          <React.Fragment key={id + '-' + questionIdx}>
            {/* Glimmer/fill + (soft) correct glow ring */}
            <div
              style={{
                ...finalAnimStyle, // Apply combined animation and slide styles
                position: "absolute",
                top: `${rect.top}px`,
                left: `${rect.left}px`,
                width: `${rect.width}px`,
                height: `${rect.height}px`,
                borderRadius: "999px",
                boxShadow: isCorrect ? "0 0 0 5px #22c55e, 0 0 25px #22c55e" : "none",
                // Remove transition from here, handled by finalAnimStyle
                zIndex: 4,
                overflow: "hidden", // Keep this to clip the glimmer
              }}
            >
              <div className="glimmer-overlay" style={{ width: `${visualPercentage}%` }}>
                <div className="reveal-glimmer-sheen" />
              </div>
            </div>

            {/* Percentage Medal */}
            <div
style={{
            position: "absolute",
            top: `${rect.top + rect.height / 2}px`,
            left: `${rect.right}px`, // Anchor to the right edge coordinate
            width: `${MEDAL_SIZE}px`,
            height: `${MEDAL_SIZE}px`,
            borderRadius: "50%",
            background: "rgba(0, 0, 0, 0.65)",
            border: "3px solid white",
            boxShadow: "0 4px 15px rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            // Correct transform: centers vertically, shifts LEFT by half its own width
            transform: `translate(-50%, -50%) ${slideTransform}`,
            transition: `${slideTransition}, opacity 0.3s, transform 0.3s`, // Combine transitions
            zIndex: 10,
            opacity: 0,
            animation: "pop-in 300ms ease-out forwards",
            animationDelay: `${REVEAL_START_DELAY_MS + displayIndex * 100 + 400}ms`,
          }}
            >
              <span
                style={{
                  fontSize: `clamp(14px, 1.2vw, ${rect.height * 0.85}px)`,
                  color: "white",
                  fontWeight: 900,
                  textShadow: "1px 1px 3px rgba(0,0,0,0.8)",
                }}
              >
                {percentage.toFixed(0)}%
              </span>
            </div>

            {/* Team selfie marquee */}
            <div
              style={{
                ...finalAnimStyle, // Apply combined animation and slide styles
                position: "absolute",
                top: `${rect.top + rect.height / 2 - 40}px`,
                left: `${rect.right + 20}px`,
                width: `calc(100vw - ${rect.right + 40}px)`,
                height: "120px",
                overflow: "hidden",
                maskImage: "linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)",
                WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)",
                zIndex: 2,
              }}
            >
              {teams.length > 0 && (
              <div
                // Marquee scrolling is disabled, just use flex
                className={teams.length > 1 && !disableScroll ? "scroll-row-horizontal" : ""}
                style={{
                  display: "flex",
                  gap: "16px",
                  "--marquee-duration": `${marqueeDuration || 16}s`,
                  "--marquee-from": `${Math.max(0, availableWidth || 0)}px`,
                  "--marquee-to": `-${Math.max(0, totalImageWidth || 0)}px`,
                }}
              >
                  {scrollingTeams.map((team, index) => (
                    <div key={`${team.team}-${id}-${index}`} style={{ padding: "0 8px" }}>
                      <TeamCard selfieUrl={team.selfieUrl} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}


    </div>
  );
}